import { useQuery } from "@tanstack/react-query";
import { useProject } from "@/hooks/useProject";

interface Template {
  id: number;
  name: string;
  jotform_form_id_referral?: string | null;
}

interface UseAvailableTemplatesProps {
  formMappingId?: number | null;
  jotformFormId?: string | null;
  enabled?: boolean;
  hasReferrals?: boolean; // If false, exclude templates that require referrals
}

export function useAvailableTemplates({
  formMappingId,
  jotformFormId,
  enabled = true,
  hasReferrals = true, // Default to true to show all templates
}: UseAvailableTemplatesProps) {
  const { projectClient, currentProject } = useProject();

  const { data: templates, isLoading, error } = useQuery({
    queryKey: ["available-templates", currentProject, formMappingId, jotformFormId, hasReferrals],
    queryFn: async () => {
      let targetJotformFormId = jotformFormId;

      // Si no tenemos jotformFormId pero tenemos formMappingId, obtenerlo
      if (!targetJotformFormId && formMappingId) {
        const { data: formMapping, error: formMappingError } = await projectClient
          .from("jotform_form_mappings")
          .select("jotform_form_id")
          .eq("id", formMappingId)
          .eq("project_id", projectClient.getProjectId())
          .single();

        if (formMappingError) {
          throw new Error(`Error al obtener el formulario: ${formMappingError.message}`);
        }

        targetJotformFormId = formMapping?.jotform_form_id || null;
      }

      if (!targetJotformFormId) {
        return [];
      }

      // Buscar todas las plantillas que tengan este jotform_form_id
      const { data: templatesData, error: templatesError } = await projectClient
        .from("report_templates_v2")
        .select("id, name, jotform_form_id_referral")
        .eq("jotform_form_id", targetJotformFormId)
        .eq("project_id", projectClient.getProjectId())
        .order("name");

      if (templatesError) {
        throw new Error(`Error al obtener plantillas: ${templatesError.message}`);
      }

      let filteredTemplates = (templatesData || []) as Template[];

      // Si no tiene referidos, excluir plantillas que requieren referidos
      // (aquellas que tienen jotform_form_id_referral configurado)
      if (!hasReferrals) {
        filteredTemplates = filteredTemplates.filter(
          (t) => !t.jotform_form_id_referral
        );
      }

      return filteredTemplates;
    },
    enabled: enabled && (!!formMappingId || !!jotformFormId),
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
    gcTime: 10 * 60 * 1000, // Mantener en caché por 10 minutos
  });

  return {
    templates: templates || [],
    isLoading,
    error,
    hasMultipleTemplates: (templates || []).length > 1,
  };
}

